
import { useNavigate } from 'react-router-dom';
import './Auth.css'; // Re-use auth styling for consistency

function FirstRun() {
    const navigate = useNavigate();

    return (
        <div className="auth-page">
            <div className="auth-container" style={{ maxWidth: '600px' }}>
                <div className="auth-header">
                    <div className="auth-logo">
                        <svg width="48" height="48" viewBox="0 0 100 100" fill="none">
                            <defs>
                                {/* Background gradient */}
                                <linearGradient id="firstRunBgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#22d3ee" />
                                    <stop offset="50%" stopColor="#3b82f6" />
                                    <stop offset="100%" stopColor="#8b5cf6" />
                                </linearGradient>
                                {/* Checkmark gradient */}
                                <linearGradient id="firstRunCheckGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#06b6d4" />
                                    <stop offset="50%" stopColor="#3b82f6" />
                                    <stop offset="100%" stopColor="#a855f7" />
                                </linearGradient>
                            </defs>

                            {/* Main container with rounded corners */}
                            <rect x="10" y="15" width="80" height="72" rx="12" ry="12" fill="url(#firstRunBgGradient)" />

                            {/* Grid cells - top row (filled) */}
                            <rect x="15" y="20" width="20" height="10" rx="2" fill="rgba(255,255,255,0.3)" />
                            <rect x="40" y="20" width="20" height="10" rx="2" fill="rgba(255,255,255,0.3)" />
                            <rect x="65" y="20" width="20" height="10" rx="2" fill="rgba(255,255,255,0.3)" />

                            {/* Grid cells - middle rows (white) */}
                            <rect x="15" y="34" width="20" height="10" rx="2" fill="white" />
                            <rect x="40" y="34" width="20" height="10" rx="2" fill="white" />
                            <rect x="65" y="34" width="20" height="10" rx="2" fill="white" />

                            <rect x="15" y="48" width="20" height="10" rx="2" fill="white" />
                            <rect x="40" y="48" width="20" height="10" rx="2" fill="white" />
                            <rect x="65" y="48" width="20" height="10" rx="2" fill="white" />

                            {/* Grid cells - bottom row (white) */}
                            <rect x="15" y="62" width="20" height="10" rx="2" fill="white" />
                            <rect x="40" y="62" width="20" height="10" rx="2" fill="white" />
                            <rect x="65" y="62" width="20" height="10" rx="2" fill="white" />

                            {/* Large checkmark */}
                            <path d="M50 53 L65 68 L95 30"
                                stroke="url(#firstRunCheckGradient)"
                                strokeWidth="8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none" />
                        </svg>
                    </div>
                    <h1>Welcome to Spreadsheetr! 🎉</h1>
                    <p>Let's get you started in just a few minutes</p>
                </div>

                <div className="auth-form" style={{ textAlign: 'left' }}>
                    <div className="alert alert-info" style={{ background: '#eff6ff', border: '1px solid #3b82f6', borderRadius: '8px', padding: '1.5rem', flexDirection: 'column' }}>
                        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', color: '#1e40af' }}>
                            How Spreadsheetr Works
                        </h3>
                        <p style={{ margin: '0 0 1rem 0', color: '#1e3a8a' }}>
                            Think of Spreadsheetr as a way to turn your Excel files into reliable, reusable tools that anyone on your team can use safely.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                                <span style={{ fontSize: '1.5rem' }}>📤</span>
                                <div>
                                    <strong style={{ color: '#1e40af' }}>Upload Your Excel File</strong>
                                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#1e3a8a' }}>
                                        We'll extract the calculation logic automatically
                                    </p>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                                <span style={{ fontSize: '1.5rem' }}>✓</span>
                                <div>
                                    <strong style={{ color: '#1e40af' }}>Review & Activate</strong>
                                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#1e3a8a' }}>
                                        Check that everything looks right, then activate
                                    </p>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                                <span style={{ fontSize: '1.5rem' }}>🚀</span>
                                <div>
                                    <strong style={{ color: '#1e40af' }}>Run Calculations Anytime</strong>
                                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#1e3a8a' }}>
                                        You and your team can use it over and over
                                    </p>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                                <span style={{ fontSize: '1.5rem' }}>🎨</span>
                                <div>
                                    <strong style={{ color: '#1e40af' }}>Format Your Output</strong>
                                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#1e3a8a' }}>
                                        Apply custom formatting, sorting, and grouping to results
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ margin: '1.5rem 0', padding: '1rem', background: '#f9fafb', borderRadius: '8px', borderLeft: '4px solid #10b981' }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', color: '#065f46' }}>
                            Why This Matters
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.95rem', color: '#047857' }}>
                            Once you create a template, your calculations become bulletproof. No more worrying about someone accidentally changing a formula or losing the file. Everything is tracked and secure.
                        </p>
                    </div>

                    <button
                        className="btn btn-primary btn-lg"
                        style={{ width: '100%', marginTop: '2rem' }}
                        onClick={() => navigate('/onboarding/create')}
                    >
                        Create Your First Template →
                    </button>

                    <div className="text-center" style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#6b7280' }}>
                        Takes about 2 minutes • You can always add more templates later
                    </div>
                </div>
            </div>

            <div className="auth-background">
                <div className="auth-bg-content">
                    <h2>What You'll Get</h2>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li style={{ marginBottom: '1rem', display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                            <span style={{ fontSize: '1.5rem' }}>✓</span>
                            <div>
                                <strong>No More "Excel Person" Dependency</strong>
                                <p style={{ margin: '0.25rem 0 0 0', opacity: 0.9 }}>
                                    Anyone can run calculations, not just the person who built the file
                                </p>
                            </div>
                        </li>
                        <li style={{ marginBottom: '1rem', display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                            <span style={{ fontSize: '1.5rem' }}>✓</span>
                            <div>
                                <strong>Zero Calculation Errors</strong>
                                <p style={{ margin: '0.25rem 0 0 0', opacity: 0.9 }}>
                                    Formulas are locked in and can't be accidentally changed
                                </p>
                            </div>
                        </li>
                        <li style={{ marginBottom: '1rem', display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                            <span style={{ fontSize: '1.5rem' }}>✓</span>
                            <div>
                                <strong>Complete Audit Trail</strong>
                                <p style={{ margin: '0.25rem 0 0 0', opacity: 0.9 }}>
                                    See who ran what calculation and when
                                </p>
                            </div>
                        </li>
                        <li style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                            <span style={{ fontSize: '1.5rem' }}>✓</span>
                            <div>
                                <strong>Professional Output Formatting</strong>
                                <p style={{ margin: '0.25rem 0 0 0', opacity: 0.9 }}>
                                    Generate beautifully formatted Excel files with custom styling, sorting, and grouping
                                </p>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default FirstRun;
